/**
 * Staged creator-sheet import review — API client for the endpoints under
 * /campaigns/{id}/creators/import* (roster:import scope, admin-only; see the
 * 2026-09-29 redesign spec §6). Wire JSON is snake_case like every other
 * endpoint in this codebase; the FE types in @discovr/types are camelCase —
 * the wire->FE mapping happens explicitly here (toImportBatchRow etc.), not
 * via a generic key-casing utility.
 *
 * mapping / rows / back respond with the FULL ImportBatch (common.WriteData(w,
 * status, batch) on the BE), so every transition (e.g. status flipping
 * mapping -> review) and every re-validated row is available to the caller
 * without a follow-up GET. Every path stays under .../creators/import/ — the
 * WAF's 8 KB body-rule exemption covers only that prefix.
 *
 * One client serves both import targets: the pipeline sheet (Creators tab)
 * and outreach's "Add creators" (Outreach tab). The target rides on preview
 * and status; every later call acts on the batch, which remembers it.
 */
import { apiClient } from '@discovr/common';
import type {
  ImportBatch,
  ImportBatchRow,
  ImportBatchStatus,
  ImportCommitResult,
  ImportPreviewResult,
  ImportStatus,
  ImportTarget,
  ExpectedField,
  DropReason,
  RowChangeSet,
  SheetColumnPreview,
  RosterMatch,
  SheetResolution,
  RosterResolutionAction,
  ImportFieldGroup,
} from '@discovr/types';

// ── Wire shapes (snake_case JSON) ─────────────────────────────────────────
interface WireExpectedField {
  key: string;
  label: string;
  required: boolean;
  is_deliverable?: boolean;
  // Absent on a legacy batch created before the redesign.
  group?: string;
}
interface WireSheetColumnPreview {
  header: string;
  sample_value: string;
  suggested_field: string;
}
interface WireRosterMatch {
  roster_entry_id: string;
  locked: boolean;
  status?: string;
  // Omitted when false.
  stats_frozen?: boolean;
  current_values: Record<string, string>;
}
// validation_errors/sheet_resolution/roster_match/roster_resolution/drop_reason are
// all `omitempty` on ImportBatchRow (import_batch.go) — absent, not zero-valued,
// on a row that hasn't reached that step yet.
interface WireImportBatchRow {
  row_number: number;
  // Newer servers no longer send it; the FE doesn't read it.
  raw?: Record<string, string>;
  values: Record<string, string>;
  username_key: string;
  validation_errors?: Record<string, string>;
  validation_warnings?: Record<string, string>;
  duplicate_group_size: number;
  sheet_resolution?: SheetResolution;
  roster_match?: WireRosterMatch | null;
  roster_resolution?: RosterResolutionAction;
  drop_reason?: DropReason;
}
// A Go nil slice marshals to JSON `null`, not `[]`, so every list on the
// wire is nullable here and read through `listOf`.
interface WireImportPreviewResult {
  import_id: string;
  sheet_columns: WireSheetColumnPreview[] | null;
  expected_fields: WireExpectedField[] | null;
  row_count: number;
  warnings?: string[] | null;
}
interface WireImportStatus {
  active_import_id?: string;
  expected_fields: WireExpectedField[] | null;
}
// mapping/deflected_rows are also `omitempty` on ImportBatch.
interface WireImportBatch {
  id: string;
  campaign_id: string;
  uploaded_by: string;
  // omitempty on the Go struct — absent when the BE couldn't (or didn't try
  // to) resolve a display name for uploaded_by.
  uploaded_by_name?: string;
  status: string;
  file_name: string;
  headers: string[] | null;
  mapping?: Record<string, string>;
  expected_fields: WireExpectedField[] | null;
  // Not `omitempty` on the Go struct (import_batch.go), but a fresh batch at
  // status "mapping" (right after /preview, before mapping is submitted) is
  // constructed with no Rows set — a nil slice marshals to JSON `null`, not
  // `[]` or an absent key. See admin_import_preview_handler.go and the fixture
  // in admin_import_batch_get_handler_test.go.
  rows?: WireImportBatchRow[] | null;
  // Absent on older servers; the only row count available while `rows` is null.
  row_count?: number;
  // Always sent by the server (not omitempty); optional here only so an older
  // server's batch still maps.
  engagement_read_as_fraction?: boolean;
  deflected_rows?: number[];
  created_at: string;
  updated_at: string;
  expires_at: string;
  // Both omitempty: a batch saved before targets existed has neither.
  target?: ImportTarget;
  outreach_import_id?: number;
}
interface WireCommitResult {
  status: string;
  total_rows: number;
  // Only an outreach-target commit names the outreach batch.
  outreach_import_id?: number;
  skipped_existing?: number;
}

// Legacy batches predate `group`; treating their fields as creator-level keeps
// them in the same section the old mapping step showed them in.
const DEFAULT_FIELD_GROUP: ImportFieldGroup = 'creator';
const DEFAULT_IMPORT_TARGET: ImportTarget = 'pipeline';
const KNOWN_FIELD_GROUPS: readonly ImportFieldGroup[] = ['creator', 'audience', 'main', 'addon'];

function isFieldGroup(value: string): value is ImportFieldGroup {
  return KNOWN_FIELD_GROUPS.some((group) => group === value);
}

/** A group this client doesn't know would drop the field from every mapping
 *  dropdown, so it is read as a creator field instead. */
function toFieldGroup(raw: string | undefined): ImportFieldGroup {
  return raw !== undefined && isFieldGroup(raw) ? raw : DEFAULT_FIELD_GROUP;
}
const TARGET_PARAM = 'target';

const KNOWN_BATCH_STATUSES: readonly ImportBatchStatus[] = [
  'mapping',
  'review',
  'committing',
  'committed',
  'sheet_review',
  'roster_review',
];

function isBatchStatus(value: string): value is ImportBatchStatus {
  return KNOWN_BATCH_STATUSES.some((status) => status === value);
}

/** A status this client doesn't know can't be routed to a step, so it is
 *  refused at the boundary instead of being cast and trusted downstream. */
function toBatchStatus(raw: string): ImportBatchStatus {
  if (isBatchStatus(raw)) return raw;
  throw new Error(`Unexpected import status from the server: "${raw}"`);
}

function listOf<T>(wire: T[] | null | undefined): T[] {
  return wire ?? [];
}

// ── Mappers ────────────────────────────────────────────────────────────────
function toExpectedField(w: WireExpectedField): ExpectedField {
  return {
    key: w.key,
    label: w.label,
    required: w.required,
    isDeliverable: w.is_deliverable,
    group: toFieldGroup(w.group),
  };
}
function toSheetColumnPreview(w: WireSheetColumnPreview): SheetColumnPreview {
  return { header: w.header, sampleValue: w.sample_value, suggestedField: w.suggested_field };
}
function toRosterMatch(w: WireRosterMatch | null | undefined): RosterMatch | null {
  if (!w) return null;
  return {
    rosterEntryId: w.roster_entry_id,
    locked: w.locked,
    status: w.status,
    statsFrozen: w.stats_frozen,
    currentValues: w.current_values,
  };
}
function toImportBatchRow(w: WireImportBatchRow): ImportBatchRow {
  return {
    rowNumber: w.row_number,
    raw: w.raw,
    values: w.values,
    usernameKey: w.username_key,
    validationErrors: w.validation_errors ?? {},
    validationWarnings: w.validation_warnings ?? {},
    duplicateGroupSize: w.duplicate_group_size,
    sheetResolution: w.sheet_resolution ?? '',
    rosterMatch: toRosterMatch(w.roster_match),
    rosterResolution: w.roster_resolution ?? '',
    dropReason: w.drop_reason ?? '',
  };
}
function toImportPreviewResult(w: WireImportPreviewResult): ImportPreviewResult {
  return {
    importId: w.import_id,
    sheetColumns: listOf(w.sheet_columns).map(toSheetColumnPreview),
    expectedFields: listOf(w.expected_fields).map(toExpectedField),
    rowCount: w.row_count,
    warnings: listOf(w.warnings),
  };
}
function toImportStatus(w: WireImportStatus): ImportStatus {
  return {
    activeImportId: w.active_import_id ?? null,
    expectedFields: listOf(w.expected_fields).map(toExpectedField),
  };
}
function toImportBatch(w: WireImportBatch): ImportBatch {
  return {
    id: w.id,
    campaignId: w.campaign_id,
    uploadedBy: w.uploaded_by,
    uploadedByName: w.uploaded_by_name ?? '',
    status: toBatchStatus(w.status),
    fileName: w.file_name,
    headers: listOf(w.headers),
    mapping: w.mapping ?? {},
    expectedFields: listOf(w.expected_fields).map(toExpectedField),
    rows: listOf(w.rows).map(toImportBatchRow),
    rowCount: w.row_count,
    engagementReadAsFraction: w.engagement_read_as_fraction ?? false,
    deflectedRows: w.deflected_rows ?? [],
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    expiresAt: w.expires_at,
    target: w.target ?? DEFAULT_IMPORT_TARGET,
    outreachImportId: w.outreach_import_id ?? null,
  };
}
function toCommitResult(w: WireCommitResult): ImportCommitResult {
  return {
    status: w.status,
    totalRows: w.total_rows,
    outreachImportId: w.outreach_import_id ?? null,
    skippedExisting: w.skipped_existing,
  };
}

const importPath = (campaignId: string, rest = '') =>
  `/campaigns/${encodeURIComponent(campaignId)}/creators/import${rest}`;

const previewImport = (target: ImportTarget) => (campaignId: string, file: File): Promise<ImportPreviewResult> => {
  const form = new FormData();
  form.append(TARGET_PARAM, target);
  form.append('file', file);
  return apiClient
    .post<{ data: WireImportPreviewResult }>(importPath(campaignId, '/preview'), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => toImportPreviewResult(res.data.data));
};

const getImportStatus = (target: ImportTarget) => (campaignId: string): Promise<ImportStatus> =>
  apiClient
    .get<{ data: WireImportStatus }>(importPath(campaignId, '/status'), { params: { [TARGET_PARAM]: target } })
    .then((res) => toImportStatus(res.data.data));

const submitMapping = (
  campaignId: string,
  importId: string,
  mapping: Record<string, string>,
): Promise<ImportBatch> =>
  apiClient
    .post<{ data: WireImportBatch }>(
      importPath(campaignId, `/${encodeURIComponent(importId)}/mapping`),
      { mapping },
    )
    .then((res) => toImportBatch(res.data.data));

function toWireChangeSet(c: RowChangeSet) {
  return {
    edits: c.edits.map((e) => ({ row_number: e.rowNumber, values: e.values })),
    deletes: c.deletes,
    restores: c.restores,
    decisions: c.decisions.map((d) => ({ row_number: d.rowNumber, choice: d.choice })),
  };
}

const saveRows = (campaignId: string, importId: string, changes: RowChangeSet): Promise<ImportBatch> =>
  apiClient
    .post<{ data: WireImportBatch }>(importPath(campaignId, `/${encodeURIComponent(importId)}/rows`), toWireChangeSet(changes))
    .then((res) => toImportBatch(res.data.data));

const stepBackImport = (campaignId: string, importId: string): Promise<ImportBatch> =>
  apiClient
    .post<{ data: WireImportBatch }>(importPath(campaignId, `/${encodeURIComponent(importId)}/back`), {})
    .then((res) => toImportBatch(res.data.data));

const commitImport = (
  campaignId: string,
  importId: string,
  opts: { skipBlocking: boolean },
): Promise<ImportCommitResult> =>
  apiClient
    .post<{ data: WireCommitResult }>(importPath(campaignId, `/${encodeURIComponent(importId)}/commit`), {
      skip_blocking: opts.skipBlocking,
    })
    .then((res) => toCommitResult(res.data.data));

const getImportBatch = (campaignId: string, importId: string): Promise<ImportBatch> =>
  apiClient
    .get<{ data: WireImportBatch }>(importPath(campaignId, `/${encodeURIComponent(importId)}`))
    .then((res) => toImportBatch(res.data.data));

const discardImport = (campaignId: string, importId: string): Promise<void> =>
  apiClient
    .delete(importPath(campaignId, `/${encodeURIComponent(importId)}`))
    .then(() => undefined);

export function createImportBatchApi(target: ImportTarget = DEFAULT_IMPORT_TARGET) {
  return {
    target,
    previewImport: previewImport(target),
    getImportStatus: getImportStatus(target),
    submitMapping,
    saveRows,
    stepBackImport,
    commitImport,
    getImportBatch,
    discardImport,
  };
}

export type ImportBatchApi = ReturnType<typeof createImportBatchApi>;
